/**
 * SessionSidebar - Retro mission control styled sidebar for chat session history.
 *
 * Shows list of past conversations with title, timestamp, and message count.
 * Allows creating new sessions and navigating to existing ones.
 */

import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useChatSessions } from '../../hooks/useChatSessions'
import { Plus, MessageSquare, RefreshCw, X } from 'lucide-react'

interface SessionSidebarProps {
  /** Drawer open state — only affects mobile (<md); desktop is always shown. */
  isOpen?: boolean
  /** Called to close the mobile drawer (backdrop tap, close button, or nav). */
  onClose?: () => void
}

function SessionSidebar({ isOpen = false, onClose }: SessionSidebarProps) {
  const navigate = useNavigate()
  const { sessionId: activeSessionId } = useParams()
  const { sessions, loading, error, refresh } = useChatSessions()

  // Auto-refresh session list when the active session changes (e.g. after creating a new chat).
  // Delay slightly so the WebSocket handler has time to call get_or_create_session() on the
  // backend before the REST fetch fires — otherwise the new session may not exist in the DB yet.
  useEffect(() => {
    if (activeSessionId) {
      const timer = setTimeout(() => refresh(), 500)
      return () => clearTimeout(timer)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only refresh on session navigation
  }, [activeSessionId])

  const handleNewChat = () => {
    const newSessionId = crypto.randomUUID()
    navigate(`/chat/${newSessionId}`)
    onClose?.()
  }

  const handleSelectSession = (sessionId: string) => {
    navigate(`/chat/${sessionId}`)
    onClose?.()
  }

  const formatTimestamp = (isoString: string) => {
    const date = new Date(isoString)
    if (isNaN(date.getTime())) return 'Unknown'
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = diffMs / (1000 * 60 * 60)

    if (diffHours < 1) return `${Math.max(1, Math.round(diffMs / (1000 * 60)))}m ago`
    if (diffHours < 24) return `${Math.round(diffHours)}h ago`
    if (diffHours < 48) return 'Yesterday'
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  return (
    <>
      {/* Backdrop — mobile only, when the drawer is open. */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/60 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`
          w-64 border-r border-retro-border bg-retro-bg flex flex-col h-full
          fixed inset-y-0 left-0 z-40 transition-transform duration-200 ease-out
          md:static md:z-auto md:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        {/* Header */}
        <div className="px-3 py-3 border-b border-retro-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold tracking-widest uppercase text-retro-dim">
              Sessions
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={refresh}
                className="p-1 rounded hover:bg-retro-surface transition-colors"
                title="Refresh sessions"
              >
                <RefreshCw className={`w-3 h-3 text-retro-dim ${loading ? 'animate-spin' : ''}`} />
              </button>
              {/* Close drawer — mobile only. */}
              <button
                onClick={onClose}
                className="p-1 rounded hover:bg-retro-surface transition-colors md:hidden"
                title="Close sessions"
              >
                <X className="w-3 h-3 text-retro-dim" />
              </button>
            </div>
          </div>
        <button
          onClick={handleNewChat}
          className="w-full flex items-center gap-2 px-3 py-2 text-xs rounded border border-retro-amber text-retro-amber hover:bg-retro-amber/10 transition-colors"
        >
          <Plus className="w-3 h-3" />
          New Chat
        </button>
      </div>

      {/* Session List */}
      <div className="flex-1 overflow-y-auto py-1">
        {error && (
          <div className="px-3 py-2 text-xs text-red-400">{error}</div>
        )}

        {!loading && sessions.length === 0 && !error && (
          <div className="px-3 py-4 text-xs text-retro-subtle text-center">
            No previous sessions
          </div>
        )}

        {sessions.map((session) => {
          const isActive = session.id === activeSessionId
          return (
            <button
              key={session.id}
              onClick={() => handleSelectSession(session.id)}
              className={`w-full text-left px-3 py-2 border-b border-retro-border/30 transition-colors ${
                isActive
                  ? 'bg-retro-amber/10 border-l-2 border-l-retro-amber'
                  : 'hover:bg-retro-surface border-l-2 border-l-transparent'
              }`}
            >
              <div className="flex items-start gap-2">
                <MessageSquare className={`w-3 h-3 mt-0.5 flex-shrink-0 ${
                  isActive ? 'text-retro-amber' : 'text-retro-dim'
                }`} />
                <div className="min-w-0 flex-1">
                  <div className={`text-xs truncate ${
                    isActive ? 'text-retro-amber' : 'text-dark-600'
                  }`}>
                    {session.title}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-retro-subtle">
                      {formatTimestamp(session.last_message_at)}
                    </span>
                    <span className="text-[10px] text-retro-subtle">
                      {session.message_count} msgs
                    </span>
                  </div>
                </div>
              </div>
            </button>
          )
        })}
        </div>
      </aside>
    </>
  )
}

export default SessionSidebar
